import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { Send, Lock, Shield, Mail, Phone, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { useLang } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const easeOut = [0.16, 1, 0.3, 1];

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    project_type: "",
    message: "",
    confidential: true,
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (
      !form.name ||
      !form.company ||
      !form.email ||
      !form.project_type ||
      !form.message
    ) {
      toast.error(lang === "es" ? "Completa los campos requeridos" : "Please fill all required fields");
      return;
    }

    if (!form.consent) {
      toast.error(t.contact.consent_required);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.success);
      setSubmitted(true);
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        country: "",
        project_type: "",
        message: "",
        confidential: true,
        consent: false,
      });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-[#0A0A0B] overflow-hidden"
    >
      {/* subtle gold glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[radial-gradient(circle,rgba(212,175,55,0.06),transparent_70%)] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: easeOut }}
            className="lg:col-span-5 lg:sticky lg:top-28 self-start"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{t.contact.eyebrow}</span>
            </div>
            <h2 className="font-display font-bold text-[clamp(2rem,4vw,3.25rem)] text-white tracking-tight leading-[1.05] max-w-[18ch]">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-[#A1A1AA] text-base leading-relaxed max-w-md">
              {t.contact.subtitle}
            </p>

            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                <div className="w-9 h-9 border border-white/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">
                    {lang === "es" ? "Confidencialidad asegurada" : "Full confidentiality"}
                  </div>
                  <div className="text-xs text-[#8B939C]">
                    {lang === "es" ? "NDA disponible a solicitud" : "NDA available on request"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                <div className="w-9 h-9 border border-white/10 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">advisory@deppcm.com</div>
                  <div className="text-xs text-[#8B939C]">
                    {lang === "es" ? "Respuesta en 1 día hábil" : "Response within 1 business day"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                <div className="w-9 h-9 border border-white/10 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">+52 (993) 000 · 0000</div>
                  <div className="text-xs text-[#8B939C]">
                    {lang === "es" ? "Línea directa ejecutiva" : "Executive direct line"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                <div className="w-9 h-9 border border-white/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">
                    Villahermosa · Houston
                  </div>
                  <div className="text-xs text-[#8B939C]">
                    {lang === "es" ? "Matriz y mesa de asesoría" : "HQ & advisory desk"}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right column: form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: easeOut, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="relative border border-white/10 bg-[#0F0F11] p-8 lg:p-12">
              {/* corner markers */}
              <div className="absolute top-4 left-4 w-5 h-5 border-t border-l border-[#D4AF37]" />
              <div className="absolute top-4 right-4 w-5 h-5 border-t border-r border-[#D4AF37]" />
              <div className="absolute bottom-4 left-4 w-5 h-5 border-b border-l border-[#D4AF37]" />
              <div className="absolute bottom-4 right-4 w-5 h-5 border-b border-r border-[#D4AF37]" />

              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="min-h-[420px] flex flex-col items-center justify-center text-center"
                >
                  <div className="w-14 h-14 border border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center">
                    <Check className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="overline mt-6">
                    {lang === "es" ? "Transmisión segura" : "Secure transmission"}
                  </div>
                  <div className="font-display font-bold text-white text-2xl lg:text-3xl mt-4 max-w-md">
                    {t.contact.success}
                  </div>
                  <button
                    data-testid="contact-reset"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-[#D4AF37] text-[11px] font-bold uppercase tracking-[0.2em] hover:text-[#F0C84B] transition-colors"
                  >
                    {lang === "es" ? "Enviar otra consulta" : "Submit another inquiry"}
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="flex items-center justify-between pb-6 border-b border-white/5">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                      <Lock className="w-3 h-3" />
                      {lang === "es" ? "Canal Cifrado" : "Encrypted Channel"}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939C]">
                      DEPP/ADVISORY/0x{Math.floor(Math.random() * 0xffff).toString(16).padStart(4, "0").toUpperCase()}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FloatingInput
                      testid="contact-name"
                      label={t.contact.fields.name}
                      value={form.name}
                      onChange={(v) => update("name", v)}
                      required
                    />
                    <FloatingInput
                      testid="contact-company"
                      label={t.contact.fields.company}
                      value={form.company}
                      onChange={(v) => update("company", v)}
                      required
                    />
                    <FloatingInput
                      testid="contact-email"
                      label={t.contact.fields.email}
                      type="email"
                      value={form.email}
                      onChange={(v) => update("email", v)}
                      required
                    />
                    <FloatingInput
                      testid="contact-phone"
                      label={t.contact.fields.phone}
                      value={form.phone}
                      onChange={(v) => update("phone", v)}
                    />
                    <FloatingInput
                      testid="contact-country"
                      label={t.contact.fields.country}
                      value={form.country}
                      onChange={(v) => update("country", v)}
                    />
                    <div className="flex flex-col">
                      <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
                        {t.contact.fields.project_type} *
                      </label>
                      <Select
                        value={form.project_type}
                        onValueChange={(v) => update("project_type", v)}
                      >
                        <SelectTrigger
                          data-testid="contact-project-type"
                          className="bg-transparent border-0 border-b border-white/15 rounded-none text-white px-0 focus:ring-0 focus:border-[#D4AF37] h-10 hover:border-white/30 transition-colors"
                        >
                          <SelectValue placeholder={lang === "es" ? "Selecciona..." : "Select..."} />
                        </SelectTrigger>
                        <SelectContent
                          className="bg-[#121214] border border-white/10 text-white rounded-none"
                        >
                          {t.contact.types.map((type) => (
                            <SelectItem
                              key={type}
                              value={type}
                              className="text-white focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                            >
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
                      {t.contact.fields.message} *
                    </label>
                    <textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder={t.contact.fields.message_ph}
                      required
                      rows={5}
                      className="bg-transparent border-0 border-b border-white/15 rounded-none text-white text-sm px-0 py-2 focus:outline-none focus:border-[#D4AF37] placeholder:text-[#3f3f46] resize-none transition-colors hover:border-white/30"
                    />
                  </div>

                  <label className="flex items-center gap-3 cursor-pointer select-none group">
                    <Checkbox
                      data-testid="contact-confidential"
                      checked={form.confidential}
                      onCheckedChange={(v) => update("confidential", !!v)}
                      className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] data-[state=checked]:text-black rounded-none"
                    />
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] group-hover:text-white transition-colors">
                      {t.contact.fields.confidential}
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer select-none group">
                    <Checkbox
                      data-testid="contact-consent"
                      checked={form.consent}
                      onCheckedChange={(v) => update("consent", !!v)}
                      className="mt-0.5 border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] data-[state=checked]:text-black rounded-none"
                    />
                    <span className="text-[11px] leading-relaxed text-[#A1A1AA] group-hover:text-white transition-colors">
                      {t.contact.fields.consent}{" "}
                      <Link
                        to="/privacidad"
                        target="_blank"
                        className="text-[#D4AF37] underline underline-offset-4 hover:text-[#F0C84B]"
                      >
                        {lang === "es" ? "Leer Aviso" : "Read Notice"}
                      </Link>
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black py-4 text-[12px] font-bold uppercase tracking-[0.25em] hover:bg-[#F0C84B] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? t.contact.submitting : t.contact.submit}
                    {!loading && (
                      <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function FloatingInput({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="flex flex-col">
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
        {label}
        {required && " *"}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="bg-transparent border-0 border-b border-white/15 rounded-none text-white text-sm px-0 py-2 focus:outline-none focus:border-[#D4AF37] placeholder:text-[#3f3f46] transition-colors hover:border-white/30"
      />
    </div>
  );
}
